import {
  BuildingIcon,
  HomeIcon,
  DropletsIcon,
  ThermometerSnowflakeIcon,
  TreePineIcon,
  ArrowUpFromLineIcon,
  LayoutGridIcon,
  AnchorIcon,
} from 'lucide-react'
import type { Service } from '../types'

export const services: Service[] = [
  {
    slug: 'foundation-repair',
    icon: BuildingIcon,
    title: { en: 'Foundation Repair', es: 'Reparación de Cimientos' },
    short: {
      en: 'Structural foundation repair for settling, cracking, and failing footings across Washington.',
      es: 'Reparación estructural de cimientos por asentamiento, grietas y bases dañadas en todo Washington.',
    },
    bullets: [
      { en: 'Structural beam repair and replacement', es: 'Reparación y reemplazo de vigas estructurales' },
      { en: 'Foundation crack sealing and repair', es: 'Sellado y reparación de grietas' },
      { en: 'Settling foundation solutions', es: 'Soluciones para cimientos hundidos' },
      { en: 'Professional assessment and restoration', es: 'Evaluación profesional y restauración' },
    ],
    keywords: ['foundation repair Washington', 'structural repair specialist', 'foundation damage repair'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f9f6a5c4-fd0d-483f-8e91-00ac70044607.jpg',
    imageAlt: {
      en: 'Repaired concrete house foundation wall with carbon fiber reinforcement in Washington',
      es: 'Muro de cimiento de concreto reparado con refuerzo de fibra de carbono en Washington',
    },
  },
  {
    slug: 'crawl-space-encapsulation',
    icon: HomeIcon,
    title: { en: 'Crawl Space Repair & Encapsulation', es: 'Reparación y Encapsulado de Crawl Space' },
    short: {
      en: 'Full crawl space cleanout, moisture barriers, and professional encapsulation that keeps damp air out.',
      es: 'Limpieza completa, barreras de humedad y encapsulado profesional que mantiene el aire húmedo afuera.',
    },
    bullets: [
      { en: 'Crawl space cleaning and remediation', es: 'Limpieza y remediación de crawl space' },
      { en: 'Professional crawl space encapsulation', es: 'Encapsulado profesional de crawl space' },
      { en: 'Moisture barrier installation', es: 'Instalación de barrera contra humedad' },
      { en: 'Vapor barrier (black plastic) installation', es: 'Instalación de barrera de vapor (plástico negro)' },
    ],
    keywords: ['crawl space repair near me', 'crawl space encapsulation', 'affordable crawl space solutions'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3ff960fe-068f-4c75-9c67-cedcad1e8785.jpg',
    imageAlt: {
      en: 'Clean encapsulated crawl space with white vapor barrier after professional repair',
      es: 'Crawl space encapsulado y limpio con barrera de vapor blanca tras reparación profesional',
    },
  },
  {
    slug: 'basement-waterproofing',
    icon: DropletsIcon,
    title: { en: 'Basement Waterproofing', es: 'Impermeabilización de Sótanos' },
    short: {
      en: 'Stop water before it reaches your living space with drainage, sump pumps, and sealed walls.',
      es: 'Detén el agua antes de que llegue a tu hogar con drenaje, bombas de sumidero y muros sellados.',
    },
    bullets: [
      { en: 'Moisture prevention systems', es: 'Sistemas de prevención de humedad' },
      { en: 'Sump pump installation and maintenance', es: 'Instalación y mantenimiento de bomba de sumidero' },
      { en: 'Basement sealing and protection', es: 'Sellado y protección del sótano' },
      { en: 'Humidity control solutions', es: 'Soluciones de control de humedad' },
    ],
    keywords: ['basement waterproofing Washington', 'basement moisture solutions', 'basement waterproofing near me'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/85d8389c-156b-4884-9b88-ae2bdbe80e71.jpg',
    imageAlt: {
      en: 'Dry waterproofed basement with new sump pump and perimeter drainage',
      es: 'Sótano impermeabilizado y seco con bomba de sumidero nueva y drenaje perimetral',
    },
  },
  {
    slug: 'insulation-installation',
    icon: ThermometerSnowflakeIcon,
    title: { en: 'Insulation Services', es: 'Servicios de Aislamiento' },
    short: {
      en: 'Energy-efficient insulation for crawl spaces, floors, and walls that cuts heat loss and lowers bills.',
      es: 'Aislamiento eficiente para crawl spaces, pisos y muros que reduce la pérdida de calor y las facturas.',
    },
    bullets: [
      { en: 'Professional insulation installation', es: 'Instalación profesional de aislamiento' },
      { en: 'Energy-efficient insulation upgrades', es: 'Mejoras de aislamiento energéticamente eficientes' },
      { en: 'Heat loss prevention', es: 'Prevención de pérdida de calor' },
      { en: 'Comfort and efficiency improvement', es: 'Mejora de confort y eficiencia' },
    ],
    keywords: ['insulation installation contractor', 'home insulation Washington', 'energy efficient insulation'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ca71cd62-de50-48ee-a1d8-9829bfdd17c6.jpg',
    imageAlt: {
      en: 'New fiberglass insulation batts installed between floor joists',
      es: 'Aislamiento de fibra de vidrio nuevo instalado entre viguetas del piso',
    },
  },
  {
    slug: 'wood-rot-repair',
    icon: TreePineIcon,
    title: { en: 'Wood Damage & Rot Repair', es: 'Reparación de Madera y Pudrición' },
    short: {
      en: "Washington's wet climate rots structural wood. We treat, replace, and protect it properly.",
      es: 'El clima húmedo de Washington pudre la madera estructural. La tratamos, reemplazamos y protegemos.',
    },
    bullets: [
      { en: 'Structural wood rot treatment', es: 'Tratamiento de pudrición estructural' },
      { en: 'Rotted wood replacement', es: 'Reemplazo de madera podrida' },
      { en: 'Beam and joist replacement', es: 'Reemplazo de vigas y viguetas' },
      { en: 'Wood protection and sealing', es: 'Protección y sellado de madera' },
    ],
    keywords: ['wood rot repair', 'wood replacement services', 'structural wood replacement'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/723630b4-8687-4dc3-bf72-8519a064a2e2.jpg',
    imageAlt: {
      en: 'New pressure-treated beams and joists installed under a home',
      es: 'Vigas y viguetas nuevas tratadas a presión instaladas bajo una casa',
    },
  },
  {
    slug: 'smart-jack-installation',
    icon: ArrowUpFromLineIcon,
    title: { en: 'Smart Jack Installation', es: 'Instalación de Smart Jacks' },
    short: {
      en: 'Adjustable steel support posts that lift sagging floors back to level and hold them there.',
      es: 'Postes de acero ajustables que levantan pisos hundidos y los mantienen nivelados.',
    },
    bullets: [
      { en: 'Adjustable support posts', es: 'Postes de soporte ajustables' },
      { en: 'Floor joist reinforcement', es: 'Refuerzo de viguetas del piso' },
      { en: 'Sagging floor correction', es: 'Corrección de pisos hundidos' },
      { en: 'Structural support systems', es: 'Sistemas de soporte estructural' },
    ],
    keywords: ['smart jack installation', 'floor support', 'sagging floor repair'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/fe0db6d0-5034-4821-8d19-fee227dfe1f1.jpg',
    imageAlt: {
      en: 'Adjustable steel smart jack posts supporting level floor joists',
      es: 'Postes de acero ajustables soportando viguetas niveladas',
    },
  },
  {
    slug: 'floor-joist-reinforcement',
    icon: LayoutGridIcon,
    title: { en: 'Floor Joist Reinforcement', es: 'Refuerzo de Viguetas de Piso' },
    short: {
      en: 'Sistered, repaired, or replaced joists that make bouncy, uneven floors solid again.',
      es: 'Viguetas reforzadas, reparadas o reemplazadas que devuelven firmeza a pisos disparejos.',
    },
    bullets: [
      { en: 'Joist repair and replacement', es: 'Reparación y reemplazo de viguetas' },
      { en: 'Floor strengthening', es: 'Refuerzo del piso' },
      { en: 'Structural reinforcement', es: 'Refuerzo estructural' },
      { en: 'Support system installation', es: 'Instalación de sistemas de soporte' },
    ],
    keywords: ['floor joist repair', 'floor reinforcement', 'sagging floors'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/fe0db6d0-5034-4821-8d19-fee227dfe1f1.jpg',
    imageAlt: {
      en: 'Reinforced level floor joists with new hardware under a house',
      es: 'Viguetas niveladas y reforzadas con herrajes nuevos bajo una casa',
    },
  },
  {
    slug: 'support-post-replacement',
    icon: AnchorIcon,
    title: { en: 'Support Post Replacement', es: 'Reemplazo de Postes de Soporte' },
    short: {
      en: 'Load-bearing posts and columns replaced on proper footings so your home carries its weight.',
      es: 'Postes y columnas de carga reemplazados sobre bases adecuadas para sostener tu hogar.',
    },
    bullets: [
      { en: 'Load-bearing post replacement', es: 'Reemplazo de postes de carga' },
      { en: 'Structural column support', es: 'Soporte de columnas estructurales' },
      { en: 'Foundation post installation', es: 'Instalación de postes de cimiento' },
      { en: 'Professional support systems', es: 'Sistemas de soporte profesionales' },
    ],
    keywords: ['support post replacement', 'structural support', 'foundation posts'],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9d43b830-86fd-47ce-abc6-101c597b74ae.jpg',
    imageAlt: {
      en: 'Technician installing a steel support post in an encapsulated crawl space',
      es: 'Técnico instalando un poste de acero en un crawl space encapsulado',
    },
  },
]

export function getService(slug?: string) {
  return services.find((s) => s.slug === slug)
}
